import { ArrowRight, Check, Star } from "lucide-react";
import { BRAND, HERO_BULLETS, HERO_IMAGE, waLink } from "@/lib/brand";

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden bg-black"
    >
      {/* Background image */}
      <img
        src={HERO_IMAGE}
        alt="Esquadrias de alumínio e vidro em Santa Catarina"
        className="absolute inset-0 w-full h-full object-cover opacity-60"
      />
      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/55 to-[#050505]" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/30 to-transparent" />

      {/* Frame lines decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-24 left-0 right-0 h-px bg-white/5" />
        <div className="absolute bottom-32 left-0 right-0 h-px bg-white/5" />
        <div className="absolute top-24 bottom-32 left-12 w-px bg-white/5 hidden lg:block" />
        <div className="absolute top-24 bottom-32 right-12 w-px bg-white/5 hidden lg:block" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-end">
          <div className="lg:col-span-8 reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#facc15]" />
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#facc15] font-bold">
                Florianópolis · São José · Palhoça · Biguaçu
              </span>
            </div>

            {/* Answer #1: O que fazem? + Answer #2: Onde atendem? */}
            <h1
              data-testid="hero-title"
              className="font-display text-4xl sm:text-5xl lg:text-[80px] xl:text-[92px] leading-[0.98] tracking-tight text-white"
            >
              Esquadrias de Alumínio
              <br />
              e{" "}
              <span className="italic font-light text-[#facc15]">
                Soluções em Vidro
              </span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-zinc-200 max-w-2xl leading-relaxed">
              Especialistas em esquadrias de alumínio em{" "}
              <span className="text-white font-semibold">Florianópolis</span> e
              região — fabricação própria, instalação especializada e garantia
              de qualidade.
            </p>

            {/* Answer #3: Por que escolher — 4 bullets */}
            <ul
              data-testid="hero-bullets"
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl"
            >
              {HERO_BULLETS.map((b, i) => (
                <li
                  key={b}
                  data-testid={`hero-bullet-${i}`}
                  className="flex items-center gap-3 text-white"
                >
                  <span className="flex items-center justify-center h-6 w-6 bg-[#facc15] text-black flex-shrink-0">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span className="text-sm sm:text-base font-medium">{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="group inline-flex items-center justify-between gap-4 px-7 py-5 bg-[#facc15] text-black font-bold uppercase text-[11px] sm:text-[12px] tracking-[0.24em] hover:bg-white transition-colors"
              >
                Solicitar Orçamento no WhatsApp
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                  strokeWidth={2}
                />
              </a>
              <a
                href="#projetos"
                data-testid="hero-projects-btn"
                className="inline-flex items-center justify-between gap-4 px-7 py-5 border border-white/25 text-white font-bold uppercase text-[11px] sm:text-[12px] tracking-[0.24em] hover:bg-white hover:text-black transition-colors"
              >
                Ver Projetos
                <ArrowRight size={18} strokeWidth={2} />
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 reveal">
            <div className="border border-white/10 bg-black/50 backdrop-blur-md p-6 lg:p-8">
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    className="fill-[#facc15] text-[#facc15]"
                    strokeWidth={1}
                  />
                ))}
                <span className="ml-2 font-display text-2xl text-white">
                  5,0
                </span>
              </div>
              <p className="text-sm text-zinc-300 leading-relaxed">
                Avaliação média dos nossos clientes em Florianópolis, São José
                e Palhoça. Atendimento, acabamento e prazo — auditados em cada
                projeto.
              </p>
              <div className="mt-6 pt-6 border-t border-white/10 flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                    WhatsApp Direto
                  </p>
                  <p className="font-display text-xl text-white mt-1">
                    {BRAND.whatsapp_display}
                  </p>
                </div>
                <a
                  href={waLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-card-whatsapp-btn"
                  className="text-[#facc15] text-xs uppercase tracking-[0.22em] font-bold hover:text-white"
                >
                  Chamar →
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 z-10">
        Role para ver mais ↓
      </div>
    </section>
  );
};
